import { push } from 'connected-react-router';
import { all, call, put, select, spawn, take } from 'redux-saga/effects';
import { accountBalanceRequest } from '../../modules/account/actions';
import { serviceUnavailable } from '../../modules/app/actions';
import { ConnectionError, NotFoundError, ServiceUnavailableError } from '../../modules/app/errors';
import { restartUserJourney } from '../../modules/auth/saga';
import { close, open } from '../../modules/modal/actions';
import { HOME } from '../../routes/paths';
import hidePeriod from '../../utils/hidePeriod';
import * as form from '../FormMessage/actions';
import i18n from '../i18n/i18n';
import { getLocale } from '../i18n/selectors';
import * as notify from '../layout/Notifications/actions';
import {
  CANCEL_REQUEST,
  cancelFailure,
  cancelSuccess,
  failure,
  HISTORY_REQUEST,
  historyFailure,
  historyRequest,
  historySuccess,
  pinFailure,
  REQUEST,
  SAVE_AMOUNT,
  success,
  VIEW_ENTER
} from './actions';
import {
  REASON_DEPOSIT_LIMITS_EXCEED,
  REASON_ONLINE_ABOVE_MAXIMUM,
  REASON_PIN_BLOCKED,
  REASON_PIN_INVALID,
  REASON_SELF_EXCLUDED,
  REASON_TOO_MANY_METHODS,
  WithdrawBadRequestError,
  WithdrawForbiddenError,
  WithdrawPendingError
} from './errors';
import { cancelWithdrawal, getWithdrawHistory, postWithdraw } from './rest';
import { getAmount } from './selectors';

/* eslint-disable no-constant-condition */
export function* watchViewEnter() {
  while (yield take(VIEW_ENTER)) {
    yield put(historyRequest());
    if (hidePeriod()) {
      yield put(accountBalanceRequest(true));
    }
  }
}

export function* watchSaveAmount() {
  while (yield take(SAVE_AMOUNT)) {
    yield put(form.clearMessage());
  }
}

export function* watchRequest() {
  while (true) {
    const { pin } = yield take(REQUEST);
    const amount = yield select(getAmount);
    yield put(form.clearMessage());
    try {
      yield put(open({ componentName: 'RequestInProgress' }));
      yield call(postWithdraw, amount, pin);
      yield put(close());
      yield put(success(amount));
      yield put(accountBalanceRequest());
      yield put(historyRequest());
    } catch (e) {
      yield put(close());
      const locale = yield select(getLocale);
      const isBadRequest = e instanceof WithdrawBadRequestError;
      const isForbidden = e instanceof WithdrawForbiddenError;
      const isNotFound = e instanceof NotFoundError;
      const isPending = e instanceof WithdrawPendingError;
      const isConnectionError = e instanceof ConnectionError;
      const isCommonError = isBadRequest || isForbidden || isNotFound || isPending
        || isConnectionError;
      const isUnavailable = e instanceof ServiceUnavailableError;
      if (isBadRequest && e.details.reason === REASON_PIN_INVALID) {
        yield put(pinFailure(e));
        yield put(form.setMessage(form.ERROR,
          i18n('common', 'incorrectPIN', locale, { attempts: e.details.remainingAttempts })
        ));
      } else if (isForbidden && e.details.reason === REASON_PIN_BLOCKED) {
        yield put(failure(e));
        yield put(form.setMessage(form.ERROR, i18n('common', 'blockedPIN', locale)));
      } else if (isForbidden && e.details.reason === REASON_TOO_MANY_METHODS) {
        yield put(failure(e));
        yield put(form.setMessage(form.ERROR, i18n('withdraw', 'errorPaymentMethods', locale)));
      } else if (isCommonError) {
        yield put(failure(e));
        yield put(form.setMessage(form.ERROR, 'WithdrawFailed', e.details, false));
      } else if (isUnavailable) {
        yield put(failure(e));
        yield put(serviceUnavailable());
      } else {
        yield put(failure(e));
        throw e;
      }
    }
  }
}

export function* watchHistoryRequest() {
  while (yield take(HISTORY_REQUEST)) {
    try {
      const history = yield call(getWithdrawHistory);
      yield put(historySuccess(history));
    } catch (e) {
      yield put(notify.add('Connection error while getting withdrawal history.', notify.ERROR));
      yield put(historyFailure(e));
    }
  }
}

export function* watchCancelRequest() {
  const locale = yield select(getLocale);
  const reasonLimitExceeded = [REASON_DEPOSIT_LIMITS_EXCEED, REASON_ONLINE_ABOVE_MAXIMUM];
  while (yield take(CANCEL_REQUEST)) {
    try {
      yield put(open({ componentName: 'RequestInProgress' }));
      yield call(cancelWithdrawal);
      yield put(push(HOME));
      yield put(close());
      yield put(form.setMessage(form.SUCCESS, i18n('withdraw', 'cancelSuccessMessage', locale)));
      yield put(cancelSuccess());
    } catch (e) {
      const isForbidden = e instanceof WithdrawForbiddenError;
      const isSelfExcluded = isForbidden && e.details.reason === REASON_SELF_EXCLUDED;
      const isTooManyMethods = isForbidden && e.details.reason === REASON_TOO_MANY_METHODS;
      const isLimitExceeded = isForbidden && reasonLimitExceeded.includes(e.details.reason);
      const isNotFound = e instanceof NotFoundError;
      const isConnectionError = e instanceof ConnectionError;
      const isUnavailable = e instanceof ServiceUnavailableError;
      yield put(close());
      if (isLimitExceeded || isConnectionError) {
        yield put(form.setMessage(form.ERROR, i18n('withdraw', 'cancelErrorMessage', locale)));
        yield put(cancelFailure(e));
      } else if (isSelfExcluded) {
        yield put(cancelFailure(e));
        yield call(restartUserJourney);
      } else if (isTooManyMethods) {
        yield put(form.setMessage(form.ERROR, i18n('withdraw', 'errorPaymentMethods', locale)));
        yield put(cancelFailure(e));
      } else if (isNotFound) {
        yield put(push(HOME));
        yield put(form.setMessage(form.ERROR, i18n('common', 'restErrorMessage', locale)));
        yield put(cancelFailure(e));
      } else if (isUnavailable) {
        yield put(cancelFailure(e));
        yield put(serviceUnavailable());
      } else {
        yield put(cancelFailure(e));
        throw e;
      }
    }
  }
}

export default function* root() {
  yield all([
    spawn(watchViewEnter),
    spawn(watchSaveAmount),
    spawn(watchRequest),
    spawn(watchHistoryRequest),
    spawn(watchCancelRequest)
  ]);
}
/* eslint-enable no-constant-condition */
